import type { FormField, FormValue, Handover } from '@tradeoutlet/shared';

/**
 * t275u: a handover's answers as calm definition-list rows. Empty or skipped answers are left out; checks read
 * "Yes" / "No" as plain text; long text (notes, faults) spans the full width under its question; exceptions (a "No"
 * on a check, or anything written in a faults / problems field) are flagged for a subtle emphasis, never a pill.
 */
export interface AnswerRow { id: string; label: string; value: string; files?: string[]; wide: boolean; flag: boolean }
export interface AnswerGroup { id: string; title: string | null; rows: AnswerRow[] }

/** Questions whose any answer is worth a second look ("Faults or breakdowns", "Damage", "Problems"…). */
const EXCEPTION_LABEL = /\b(faults?|breakdowns?|damage[ds]?|problems?|defects?|incidents?|accidents?|near miss(es)?|concerns?|injur(y|ies)|hazards?|spill(s|age)?)\b/i;
/** Text this long, or on several lines, wraps under its question instead of sitting in the right-hand column. */
const WIDE_CHARS = 60;
/** "None", "n/a" and the like in a faults field aren't an exception. */
const NOTHING = /^(none|no|nil|n\/?a|nothing|-+|0)\.?$/i;

const isEmpty = (v: FormValue | null | undefined) => v === undefined || v === null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && !v.length);

export function answerRow(f: Pick<FormField, 'id' | 'label' | 'type' | 'unit'>, v: FormValue | null | undefined): AnswerRow | null {
  if (isEmpty(v)) return null;
  if (f.type === 'file') return { id: f.id, label: f.label, value: '', files: v as string[], wide: true, flag: false };
  let value: string;
  if (f.type === 'checkbox' || typeof v === 'boolean') value = v ? 'Yes' : 'No';
  else if (Array.isArray(v)) value = v.join(', ');
  else value = `${String(v).trim()}${f.unit ? ` ${f.unit}` : ''}`;
  const asksAboutProblems = EXCEPTION_LABEL.test(f.label);
  const yesNo = f.type === 'checkbox' || typeof v === 'boolean' || (f.type === 'single_select' && /^(yes|no)$/i.test(value));
  // A check normally wants "Yes" ("All roller doors locked"); one that asks about problems ("Any damage?") wants "No".
  const flag = yesNo ? /^no$/i.test(value) !== asksAboutProblems
    : asksAboutProblems && f.type !== 'number' && !NOTHING.test(value);
  const wide = f.type === 'long_text' || value.includes('\n') || value.length > WIDE_CHARS || (!yesNo && asksAboutProblems && flag);
  return { id: f.id, label: f.label, value, wide, flag };
}

/** Structured sections of an older (non-template) handover, in display order. */
export const LEGACY_SECTIONS = [
  { key: 'issues', label: 'Issues' }, { key: 'stock', label: 'Stock' }, { key: 'security', label: 'Security' },
  { key: 'incidents', label: 'Incidents' }, { key: 'notes', label: 'Notes' },
] as const;

/** Every answered question, grouped by the template's sections (a group's title only when there are several). */
export function handoverAnswerGroups(h: Pick<Handover, 'form_sections' | 'answers' | 'photo_ids'> & Partial<Record<(typeof LEGACY_SECTIONS)[number]['key'], string | null>>): AnswerGroup[] {
  if (h.form_sections && h.answers) {
    const groups = h.form_sections.map(s => ({ id: s.id, title: s.title?.trim() || null, rows: s.fields.map(f => answerRow(f, h.answers![f.id])).filter((r): r is AnswerRow => r !== null) }))
      .filter(g => g.rows.length);
    const named = groups.length > 1;
    const inForm = new Set(groups.flatMap(g => g.rows.flatMap(r => r.files ?? [])));
    const loose = h.photo_ids.filter(p => !inForm.has(p));
    if (loose.length) groups.push({ id: 'photos', title: null, rows: [{ id: 'photos', label: 'Photos', value: '', files: loose, wide: true, flag: false }] });
    return groups.map(g => ({ ...g, title: named ? g.title : null }));
  }
  const rows = LEGACY_SECTIONS.map(s => answerRow({ id: s.key, label: s.label, type: 'long_text' }, h[s.key] ?? null)).filter((r): r is AnswerRow => r !== null);
  // Free-text sections are written as sentences; only "Issues" and "Incidents" carry exceptions.
  for (const r of rows) r.flag = (r.id === 'issues' || r.id === 'incidents') && !NOTHING.test(r.value);
  if (h.photo_ids.length) rows.push({ id: 'photos', label: 'Photos', value: '', files: h.photo_ids, wide: true, flag: false });
  return rows.length ? [{ id: 'legacy', title: null, rows }] : [];
}

/** t279u: the entry's title - the template it was written on ("Opening checks"); older free-text handovers are "Handover". */
export const handoverTitle = (h: Pick<Handover, 'form_name'>) => h.form_name?.trim() || 'Handover';

/** t279u: search over what the entry shows - template, author, building, team, position and the answers (not ids or keys). */
export function matchesHandover(h: Handover, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const answers = handoverAnswerGroups(h).flatMap(g => [g.title ?? '', ...g.rows.flatMap(r => [r.label, r.value])]);
  return [handoverTitle(h), h.author_name, h.location_name, h.team_name, h.position_name, ...answers]
    .some(s => typeof s === 'string' && s.toLowerCase().includes(needle));
}
